import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import PageHead from "@/components/PageHead";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ArrowLeft, RefreshCw } from "lucide-react";
import {
  CAMPAIGNS,
  COUPONS,
  findCoupon,
  formatMoney,
  POLICY_SUMMARY,
  PRICING_VERSION,
  REGIONS,
  round2,
} from "@/pricing";

type PaymentRow = {
  id: string;
  created_at: string;
  final_price: number | null;
  list_price: number | null;
  discount_applied: number | null;
  currency: string | null;
  payment_status: string | null;
  payment_provider: string | null;
  scholarship_code: string | null;
  organization_id: string | null;
  plan_code: string | null;
};

type Bucket = {
  key: string;
  label: string;
  orders: number;
  paid: number;
  revenue: number;
  discount: number;
};

const inr = (n: number) => formatMoney(round2(n), "INR");

function bucketise(
  rows: PaymentRow[],
  keyOf: (r: PaymentRow) => { key: string; label: string } | null,
): Bucket[] {
  const map = new Map<string, Bucket>();
  for (const r of rows) {
    const k = keyOf(r);
    if (!k) continue;
    const b =
      map.get(k.key) ??
      { key: k.key, label: k.label, orders: 0, paid: 0, revenue: 0, discount: 0 };
    b.orders += 1;
    if ((r.payment_status || "").toLowerCase() === "success") {
      b.paid += 1;
      b.revenue += Number(r.final_price) || 0;
      b.discount += Number(r.discount_applied) || 0;
    }
    map.set(k.key, b);
  }
  return [...map.values()].sort((a, b) => b.revenue - a.revenue || b.orders - a.orders);
}

function BucketTable({
  rows,
  firstHeader,
  empty,
}: {
  rows: Bucket[];
  firstHeader: string;
  empty: string;
}) {
  if (rows.length === 0) {
    return <p className="py-8 text-center text-sm text-muted-foreground">{empty}</p>;
  }
  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>{firstHeader}</TableHead>
          <TableHead className="text-right">Orders</TableHead>
          <TableHead className="text-right">Paid</TableHead>
          <TableHead className="text-right">Conversion</TableHead>
          <TableHead className="text-right">Revenue</TableHead>
          <TableHead className="text-right">Discount given</TableHead>
          <TableHead className="text-right">Avg order</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {rows.map((b) => (
          <TableRow key={b.key}>
            <TableCell className="font-medium">{b.label}</TableCell>
            <TableCell className="text-right">{b.orders}</TableCell>
            <TableCell className="text-right">{b.paid}</TableCell>
            <TableCell className="text-right">
              {b.orders ? `${Math.round((b.paid / b.orders) * 100)}%` : "—"}
            </TableCell>
            <TableCell className="text-right">{inr(b.revenue)}</TableCell>
            <TableCell className="text-right">{inr(b.discount)}</TableCell>
            <TableCell className="text-right">
              {b.paid ? inr(b.revenue / b.paid) : "—"}
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}

const AdminPricingAnalytics = () => {
  const [rows, setRows] = useState<PaymentRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [orgNames, setOrgNames] = useState<Record<string, string>>({});

  const load = async () => {
    setLoading(true);
    const { data } = await supabase
      .from("payments")
      .select(
        "id, created_at, final_price, list_price, discount_applied, currency, payment_status, payment_provider, scholarship_code, organization_id, plan_code",
      )
      .order("created_at", { ascending: false })
      .limit(5000);
    setRows((data as PaymentRow[]) ?? []);

    const orgIds = [...new Set(((data as PaymentRow[]) ?? []).map((r) => r.organization_id).filter(Boolean))];
    if (orgIds.length > 0) {
      const { data: orgs } = await supabase
        .from("organizations")
        .select("id, name")
        .in("id", orgIds as string[]);
      setOrgNames(Object.fromEntries((orgs ?? []).map((o) => [o.id, o.name])));
    }
    setLoading(false);
  };

  useEffect(() => {
    load();
  }, []);

  const paid = useMemo(
    () => rows.filter((r) => (r.payment_status || "").toLowerCase() === "success"),
    [rows],
  );

  const kpis = useMemo(() => {
    const revenue = paid.reduce((s, r) => s + (Number(r.final_price) || 0), 0);
    const discount = paid.reduce((s, r) => s + (Number(r.discount_applied) || 0), 0);
    const withCode = rows.filter((r) => !!r.scholarship_code);
    const paidWithCode = withCode.filter(
      (r) => (r.payment_status || "").toLowerCase() === "success",
    );
    return {
      revenue,
      discount,
      orders: rows.length,
      paid: paid.length,
      aov: paid.length ? revenue / paid.length : 0,
      couponConversion: withCode.length
        ? Math.round((paidWithCode.length / withCode.length) * 100)
        : null,
      couponShare: paid.length
        ? Math.round((paidWithCode.length / paid.length) * 100)
        : 0,
      discountRate: revenue + discount ? Math.round((discount / (revenue + discount)) * 100) : 0,
    };
  }, [rows, paid]);

  const byCoupon = useMemo(
    () =>
      bucketise(rows, (r) =>
        r.scholarship_code
          ? {
              key: r.scholarship_code,
              label: `${r.scholarship_code}${
                findCoupon(r.scholarship_code) ? "" : " (retired / ad-hoc)"
              }`,
            }
          : null,
      ),
    [rows],
  );

  const byCampaign = useMemo(
    () =>
      bucketise(rows, (r) => {
        const rule = r.scholarship_code ? findCoupon(r.scholarship_code) : null;
        if (rule?.layer === "campaign") {
          const campaign = CAMPAIGNS.find((c) => c.label === rule.label || c.id.startsWith(rule.id));
          return { key: campaign?.id ?? rule.id, label: campaign?.label ?? rule.label };
        }
        return { key: "__none", label: "No campaign (list price or other layer)" };
      }),
    [rows],
  );

  const byLayer = useMemo(
    () =>
      bucketise(rows, (r) => {
        const rule = r.scholarship_code ? findCoupon(r.scholarship_code) : null;
        const layer = rule?.layer ?? (r.scholarship_code ? "coupon" : "none");
        return {
          key: layer,
          label: layer === "none" ? "Full price (no discount layer)" : layer,
        };
      }),
    [rows],
  );

  const byPartner = useMemo(
    () =>
      bucketise(rows, (r) => {
        const rule = r.scholarship_code ? findCoupon(r.scholarship_code) : null;
        if (!rule?.partnerOrg) return null;
        return { key: rule.partnerOrg, label: rule.partnerOrg };
      }),
    [rows],
  );

  const byInstitution = useMemo(
    () =>
      bucketise(rows, (r) =>
        r.organization_id
          ? { key: r.organization_id, label: orgNames[r.organization_id] ?? r.organization_id }
          : null,
      ),
    [rows, orgNames],
  );

  const byRegion = useMemo(
    () =>
      bucketise(rows, (r) => {
        const currency = (r.currency || "INR").toUpperCase();
        const region = Object.values(REGIONS).find((x) => x.currency === currency);
        return { key: currency, label: `${region?.label ?? currency} · ${currency}` };
      }),
    [rows],
  );

  const topDiscounts = useMemo(
    () => [...byCoupon].sort((a, b) => b.discount - a.discount).slice(0, 10),
    [byCoupon],
  );

  return (
    <div className="min-h-screen bg-background p-4 md:p-8">
      <PageHead
        title="Pricing Analytics — Admin"
        description="Revenue, coupon and campaign performance under Pricing Policy v2."
        path="/admin/pricing-analytics"
        noIndex
      />
      <div className="mx-auto max-w-6xl space-y-6">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <Button asChild variant="ghost" size="sm" className="mb-2 -ml-2">
              <Link to="/admin">
                <ArrowLeft className="mr-1 h-4 w-4" /> Admin Hub
              </Link>
            </Button>
            <h1 className="text-2xl font-bold">Pricing Analytics</h1>
            <p className="text-sm text-muted-foreground">
              Pricing Policy v{PRICING_VERSION} · {paid.length} paid orders of {rows.length}
            </p>
          </div>
          <Button variant="outline" size="sm" onClick={load} disabled={loading}>
            <RefreshCw className={`mr-2 h-4 w-4 ${loading ? "animate-spin" : ""}`} /> Refresh
          </Button>
        </div>

        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {[
            { label: "Revenue collected", value: inr(kpis.revenue) },
            { label: "Average order value", value: inr(kpis.aov) },
            { label: "Discount given", value: `${inr(kpis.discount)} · ${kpis.discountRate}%` },
            {
              label: "Coupon conversion",
              value: kpis.couponConversion === null ? "—" : `${kpis.couponConversion}%`,
            },
          ].map((k) => (
            <Card key={k.label}>
              <CardHeader className="pb-2">
                <CardTitle className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                  {k.label}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-2xl font-bold">{k.value}</p>
              </CardContent>
            </Card>
          ))}
        </div>

        <Card>
          <CardHeader>
            <CardTitle className="text-base">Pricing policy in force</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <ul className="space-y-1 text-sm text-muted-foreground">
              {POLICY_SUMMARY.map((line) => (
                <li key={line}>· {line}</li>
              ))}
            </ul>
            <div className="flex flex-wrap gap-2">
              {Object.values(REGIONS).map((r) => (
                <Badge key={r.code} variant={r.active ? "default" : "outline"}>
                  {r.label}: {formatMoney(r.listPrice, r.currency)}
                  {r.active ? "" : " (not launched)"}
                </Badge>
              ))}
            </div>
            <div className="flex flex-wrap gap-2">
              {COUPONS.filter((c) => c.active !== false && !c.internalOnly).map((c) => (
                <Badge key={c.id} variant="secondary">
                  {c.id} · {c.layer}
                </Badge>
              ))}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <Tabs defaultValue="coupon">
              <TabsList className="flex w-full flex-wrap">
                <TabsTrigger value="coupon">By coupon</TabsTrigger>
                <TabsTrigger value="campaign">By campaign</TabsTrigger>
                <TabsTrigger value="layer">By layer</TabsTrigger>
                <TabsTrigger value="partner">By partner</TabsTrigger>
                <TabsTrigger value="institution">By institution</TabsTrigger>
                <TabsTrigger value="region">By region</TabsTrigger>
                <TabsTrigger value="top">Top discounts</TabsTrigger>
              </TabsList>

              <TabsContent value="coupon" className="mt-4">
                <BucketTable rows={byCoupon} firstHeader="Coupon" empty="No coupon has been used yet." />
              </TabsContent>
              <TabsContent value="campaign" className="mt-4">
                <BucketTable rows={byCampaign} firstHeader="Campaign" empty="No orders yet." />
              </TabsContent>
              <TabsContent value="layer" className="mt-4">
                <BucketTable rows={byLayer} firstHeader="Discount layer" empty="No orders yet." />
              </TabsContent>
              <TabsContent value="partner" className="mt-4">
                <BucketTable rows={byPartner} firstHeader="Partner" empty="No partner-priced orders yet." />
              </TabsContent>
              <TabsContent value="institution" className="mt-4">
                <BucketTable
                  rows={byInstitution}
                  firstHeader="Institution"
                  empty="No institution-billed orders yet."
                />
              </TabsContent>
              <TabsContent value="region" className="mt-4">
                <BucketTable rows={byRegion} firstHeader="Region" empty="No orders yet." />
              </TabsContent>
              <TabsContent value="top" className="mt-4">
                <BucketTable rows={topDiscounts} firstHeader="Coupon" empty="No discounts given yet." />
              </TabsContent>
            </Tabs>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default AdminPricingAnalytics;
